import { AlertTriangle, TriangleAlert, CheckCircle2, Clock, Circle, User } from "lucide-react";
import RoleBadge from "../Layout/RoleBadge";
import { overloadLevel } from "../../utils/assignmentStorage";

const LEVEL_STYLE = {
  OK: {
    card: "border-gray-200 bg-white",
    chip: null,
    label: null,
  },
  MEDIUM: {
    card: "border-amber-300 bg-amber-50/40",
    chip: "border-amber-300 bg-amber-100 text-amber-900",
    icon: AlertTriangle,
    label: "Beban mulai berlebih",
  },
  HIGH: {
    card: "border-red-300 bg-red-50/40",
    chip: "border-red-300 bg-red-100 text-red-900",
    icon: TriangleAlert,
    label: "Overload — segera realokasikan",
  },
};

function MiniStat({ icon: Icon, value, label, color }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={`h-3.5 w-3.5 ${color}`} strokeWidth={2} />
      <span className="text-[12px] font-semibold text-slate-800">{value}</span>
      <span className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</span>
    </div>
  );
}

function WorkloadCard({ row }) {
  const level = overloadLevel(row.total);
  const style = LEVEL_STYLE[level];
  const Icon = style.icon;

  return (
    <div
      data-testid={`workload-card-${row.user.id}`}
      data-overload={level}
      className={`rounded-lg border p-4 transition-shadow hover:shadow-sm ${style.card}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e8edf2] text-[#1e3a5f]">
            <User className="h-4 w-4" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-slate-900" title={row.user.name}>
              {row.user.name}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5">
              <RoleBadge role={row.user.role} />
              <span className="truncate text-[10.5px] text-slate-500">{row.user.nip || ""}</span>
            </div>
          </div>
        </div>
        <div
          className={`shrink-0 inline-flex items-center justify-center rounded-md font-display text-lg font-bold ${
            level === "HIGH"
              ? "bg-red-100 text-red-800"
              : level === "MEDIUM"
              ? "bg-amber-100 text-amber-800"
              : "bg-[#1e3a5f]/10 text-[#1e3a5f]"
          } h-10 w-10`}
          data-testid={`workload-total-${row.user.id}`}
        >
          {row.total}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <MiniStat icon={CheckCircle2} value={row.selesai} label="Selesai" color="text-emerald-600" />
        <MiniStat icon={Clock} value={row.onProgress} label="On Progress" color="text-blue-600" />
        <MiniStat icon={Circle} value={row.belumMulai} label="Belum" color="text-gray-500" />
        <MiniStat icon={TriangleAlert} value={row.terhambat} label="Terhambat" color="text-red-600" />
      </div>

      {style.chip ? (
        <div
          className={`mt-3 flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-semibold ${style.chip}`}
          data-testid={`overload-chip-${row.user.id}`}
        >
          <Icon className="h-3.5 w-3.5" />
          {style.label}
        </div>
      ) : null}
    </div>
  );
}

export default function BebanKerjaCards({ rows }) {
  // Sort: ANGGOTA first (spec says "per anggota"), then by total desc
  const orderRole = { ANGGOTA_TIM: 0, KETUA_TIM: 1, DALNIS: 2, IRBAN: 3 };
  const sorted = [...rows].sort((a, b) => {
    const ra = orderRole[a.user.role] ?? 99;
    const rb = orderRole[b.user.role] ?? 99;
    if (ra !== rb) return ra - rb;
    return b.total - a.total;
  });

  return (
    <div
      data-testid="beban-kerja-grid"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
    >
      {sorted.map((row) => (
        <WorkloadCard key={row.user.id} row={row} />
      ))}
    </div>
  );
}
